/* ==========================================================================
   Suki x Invisible Building 2.0 - black-friday.js

   The Black Friday advent takeover (sections/black-friday-advent.liquid).
   Loaded `async` by that section only, straight after its markup, so it
   ships for the season and goes when the section does. It stands alone
   rather than registering with theme.js: a deferred script waits behind
   every other deferred script on the page (Recharge, Hotjar), which left
   the countdown blank for seconds. It boots on whatever [data-bf] is
   already parsed, again on DOMContentLoaded, and on the theme editor's
   shopify:section:load.

   Hook contract
   ------------------------------------------------------------------------
   Root     [data-bf] with data-label-opens / -closes / -done / -copied;
            data-bf-auto-hide (hidden once every door is past);
            data-bf-preview="YYYY-MM-DD[ HH:MM]" (theme editor only), or
            ?bf_preview= on the URL, fixes the clock at that UK time
   Doors    [data-bf-door] with data-start / data-end ("YYYY-MM-DDTHH:MM:SS",
            UK wall time), data-num, data-opens; data-state is rewritten to
            locked | live | past every second, .is-open is written, and
            data-next marks the next door up while none is live
   Leaves   button[data-bf-leaf] opens its own .bf-pane (.is-open) on a live
            door, or peeks on a locked one (announcing the opening date in
            [data-bf-status]); a triple door opens tier by tier. Only one
            pane is open at a time, page-wide. The door is .is-open while
            any pane is. [data-bf-close] shuts them all
   Insides  [data-bf-inside] are inert while their pane (or, for the foot,
            every pane) is shut
   Clock    [data-bf-count] (unhidden here), [data-bf-count-label],
            [data-bf-unit="d|h|m|s"]
   Flash    template[data-bf-flash][data-start][data-end] (collection
            grids): its [data-bf-flash-item]s are copied, in turn, into
            each [data-product-card-media] of the section while the dates
            hold (or with data-bf-flash-always)
   Tea      fieldset[data-bf-tea][data-start][data-end] (product form):
            unhidden, its radios enabled, while the dates hold (or with
            data-bf-tea-always)
   Spans    [data-bf-span] with data-start / data-end (the calendar strip on
            sections/black-friday-drop.liquid) get data-state like a door
   Rail     below 990px .bf__doors swipes; each button[data-bf-rail-to=
            "<door id>"] scrolls to its door, gets the door's data-state,
            and aria-current while its door is first in view
   Code     button[data-bf-copy] copies its value (unhidden here)
   Skip     a[data-bf-skip] scrolls to the section after the takeover
   Cursor   with motion and a fine pointer, .bf--cursor on the root and a
            .bf-cursor label (data-cursor-live / -locked / -past) easing
            after the pointer over the door leaves
   Preview  while ?bf_preview= or data-bf-preview moves the clock, opened
            doors are not remembered or restored
   Motion   (.bf--motion) a changed clock digit gets .is-tick; pointer
            moves over the root write --mx / --my (-1 to 1) for any
            stickers' parallax
   ========================================================================== */

if (!window.__sukiBlackFriday) {
  window.__sukiBlackFriday = true;

  (function () {
    'use strict';

    var doc = document;
    var reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    function prefersReducedMotion() { return Boolean(reducedMotion && reducedMotion.matches); }
    function designMode() { return Boolean(window.Shopify && window.Shopify.designMode); }

    var each = function (list, fn) { Array.prototype.forEach.call(list || [], fn); };
    var STORE_KEY = 'suki:bf-open';

    /* Epoch ms for a UK wall-clock string: take it as UTC, then subtract
       London's offset at that instant (0 in November, 1h in BST). */
    var londonParts = null;
    try {
      londonParts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London', hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
    } catch (err) {
      londonParts = null;
    }

    function londonOffset(ms) {
      if (!londonParts) return 0;
      var p = {};
      each(londonParts.formatToParts(new Date(ms)), function (part) { p[part.type] = Number(part.value); });
      return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(ms / 1000) * 1000;
    }

    function parseUk(str) {
      var m = String(str || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
      if (!m) return NaN;
      var guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0));
      return guess - londonOffset(guess);
    }

    function readOpened() {
      try { return JSON.parse(localStorage.getItem(STORE_KEY)) || {}; } catch (err) { return {}; }
    }

    function remember(key) {
      try {
        var opened = readOpened();
        opened[key] = 1;
        localStorage.setItem(STORE_KEY, JSON.stringify(opened));
      } catch (err) { /* private window: the door just starts shut next time */ }
    }

    function pad(n) { return (n < 10 ? '0' : '') + n; }

    function setup(root) {
      if (root.hasAttribute('data-bf-ready')) return;
      root.setAttribute('data-bf-ready', '');

      var previewRaw = root.getAttribute('data-bf-preview');
      try {
        var param = new URLSearchParams(window.location.search).get('bf_preview');
        if (param) previewRaw = param;
      } catch (err) { /* old browser: no URL preview */ }
      // A preview date pins the starting clock; it still ticks from there.
      var skew = 0;
      if (previewRaw) {
        var previewAt = parseUk(previewRaw.length === 10 ? previewRaw + 'T00:00:01' : previewRaw);
        if (!isNaN(previewAt)) skew = previewAt - Date.now();
      }
      function now() { return Date.now() + skew; }
      function previewing() { return skew !== 0; }

      var doors = [];
      each(root.querySelectorAll('[data-bf-door]'), function (el) {
        var start = parseUk(el.getAttribute('data-start'));
        var end = parseUk(el.getAttribute('data-end'));
        if (isNaN(start) || isNaN(end)) return;
        doors.push({
          el: el,
          start: start,
          end: end,
          num: el.getAttribute('data-num') || '',
          opens: el.getAttribute('data-opens') || '',
          key: el.id + '@' + el.getAttribute('data-start'),
          leaves: el.querySelectorAll('[data-bf-leaf]'),
          panes: el.querySelectorAll('.bf-pane'),
          foot: el.querySelector('.bf-door__foot'),
          tab: root.querySelector('[data-bf-rail-to="' + el.id + '"]'),
          state: ''
        });
      });
      if (!doors.length) return;

      // Calendar chips on the drop landing pages: their own dates, no doors.
      var spans = [];
      each(root.querySelectorAll('[data-bf-span]'), function (el) {
        var a = parseUk(el.getAttribute('data-start'));
        var b = parseUk(el.getAttribute('data-end'));
        if (!isNaN(a) && !isNaN(b)) spans.push({ el: el, start: a, end: b });
      });

      var count = root.querySelector('[data-bf-count]');
      var countLabel = root.querySelector('[data-bf-count-label]');
      var units = {};
      each(root.querySelectorAll('[data-bf-unit]'), function (el) { units[el.getAttribute('data-bf-unit')] = el; });
      var status = root.querySelector('[data-bf-status]');
      var opened = readOpened();
      var motion = root.classList.contains('bf--motion') && !prefersReducedMotion();

      /* Each pane opens on its own (a triple door reveals its tiers one by
         one); the door is .is-open, and its foot usable, while any is. */
      function setPane(pane, open) {
        pane.classList.toggle('is-open', open);
        var leaf = pane.querySelector('[data-bf-leaf]');
        var inside = pane.querySelector('[data-bf-inside]');
        if (leaf) leaf.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (inside) inside.inert = !open;
      }

      function syncDoor(door) {
        var any = Array.prototype.some.call(door.panes, function (pane) { return pane.classList.contains('is-open'); });
        door.el.classList.toggle('is-open', any);
        if (door.foot) door.foot.inert = !any;
      }

      function setOpen(door, open) {
        each(door.panes, function (pane) { setPane(pane, open); });
        syncDoor(door);
      }

      function paint(door, state) {
        if (door.state === state) return;
        door.state = state;
        door.el.setAttribute('data-state', state);
        if (door.tab) door.tab.setAttribute('data-state', state);
        each(door.leaves, function (leaf) {
          if (state === 'live') leaf.removeAttribute('aria-disabled');
          else leaf.setAttribute('aria-disabled', 'true');
        });
        setOpen(door, state === 'live' && !previewing() && Boolean(opened[door.key]));
      }

      function setUnit(name, value) {
        var el = units[name];
        if (!el || el.textContent === value) return;
        el.textContent = value;
        if (!motion) return;
        el.classList.remove('is-tick');
        void el.offsetWidth;
        el.classList.add('is-tick');
      }

      function label(attr, num) {
        return (root.getAttribute(attr) || '').replace('[n]', num);
      }

      function tick() {
        var t = now();
        var live = null;
        var next = null;
        spans.forEach(function (span) {
          var spanState = t < span.start ? 'locked' : t > span.end ? 'past' : 'live';
          if (span.el.getAttribute('data-state') !== spanState) span.el.setAttribute('data-state', spanState);
        });
        doors.forEach(function (door) {
          var state = t < door.start ? 'locked' : t > door.end ? 'past' : 'live';
          paint(door, state);
          if (state === 'live' && !live) live = door;
          if (state === 'locked' && (!next || door.start < next.start)) next = door;
        });

        // With nothing live, the next door up stays in focus as the teaser.
        var teaser = live ? null : next;
        doors.forEach(function (door) {
          var flag = door === teaser;
          if (door.el.hasAttribute('data-next') !== flag) door.el.toggleAttribute('data-next', flag);
        });

        if (!live && !next) {
          if (root.hasAttribute('data-bf-auto-hide') && !designMode()) root.hidden = true;
          if (countLabel) countLabel.textContent = label('data-label-done', '');
          if (count) count.querySelector('[role="timer"]').hidden = true;
          if (count) count.hidden = false;
          // Keep ticking while a calendar chip still has a change to come.
          return spans.some(function (span) { return t <= span.end; });
        }

        // A live door counts down to its close; otherwise the next door's open.
        var target = live ? live.end : next.start;
        var text = live ? label('data-label-closes', live.num) : label('data-label-opens', next.num);
        if (countLabel && countLabel.textContent !== text) countLabel.textContent = text;
        var left = Math.max(0, Math.floor((target - t) / 1000));
        setUnit('d', pad(Math.floor(left / 86400)));
        setUnit('h', pad(Math.floor(left / 3600) % 24));
        setUnit('m', pad(Math.floor(left / 60) % 60));
        setUnit('s', pad(left % 60));
        if (count) count.hidden = false;
        return true;
      }

      doors.forEach(function (door) {
        each(door.leaves, function (leaf) {
          leaf.addEventListener('click', function () {
            // Live opens the offer; locked lets you peek at it, with an email
            // reminder in place of the button. Only a live open is remembered.
            if (door.state === 'live' || door.state === 'locked') {
              var pane = leaf.closest('.bf-pane');
              // One open at a time: shut every other pane, on any door.
              doors.forEach(function (other) {
                each(other.panes, function (p) {
                  if (p !== pane && p.classList.contains('is-open')) setPane(p, false);
                });
                syncDoor(other);
              });
              setPane(pane, true);
              syncDoor(door);
              if (door.state === 'live' && !previewing()) {
                remember(door.key);
                opened[door.key] = 1;
              }
              if (door.state === 'locked' && status) {
                status.textContent = door.num + ': ' + (leaf.querySelector('.bf-when--locked') || {}).textContent;
              }
              var inside = pane.querySelector('[data-bf-inside]');
              var first = inside && inside.querySelector('h3, .bf-inside__reward');
              if (first) first.setAttribute('tabindex', '-1');
              // Focus lands behind the door once it has lifted.
              window.setTimeout(function () { if (first) first.focus({ preventScroll: true }); }, prefersReducedMotion() ? 0 : 500);
              return;
            }
            // A tap on an ended door brings its picture into focus for a
            // moment (hover does this with a mouse).
            leaf.classList.add('is-peek');
            window.clearTimeout(leaf.__bfPeek);
            leaf.__bfPeek = window.setTimeout(function () { leaf.classList.remove('is-peek'); }, 2500);
          });
          leaf.addEventListener('animationend', function () { leaf.classList.remove('is-nudged'); });
        });
      });

      doors.forEach(function (door) {
        each(door.el.querySelectorAll('[data-bf-close]'), function (button) {
          button.hidden = false;
          button.addEventListener('click', function () {
            setOpen(door, false);
            if (door.leaves[0]) door.leaves[0].focus({ preventScroll: true });
          });
        });
      });

      each(root.querySelectorAll('[data-bf-copy]'), function (button) {
        if (!navigator.clipboard) return;
        button.hidden = false;
        var idle = button.textContent;
        button.addEventListener('click', function () {
          navigator.clipboard.writeText(button.getAttribute('data-bf-copy')).then(function () {
            button.textContent = root.getAttribute('data-label-copied') || idle;
            window.setTimeout(function () { button.textContent = idle; }, 2000);
          });
        });
      });

      var skip = root.querySelector('[data-bf-skip]');
      var after = skip && doc.getElementById(skip.getAttribute('href').slice(1));
      if (skip && after) {
        skip.addEventListener('click', function (event) {
          event.preventDefault();
          after.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
          after.focus({ preventScroll: true });
        });
      }

      if (motion && root.querySelector('.bf-sticker') && window.matchMedia('(pointer: fine)').matches) {
        var pointer = null;
        root.addEventListener('pointermove', function (event) {
          var queued = pointer;
          pointer = event;
          if (queued) return;
          window.requestAnimationFrame(function () {
            var box = root.getBoundingClientRect();
            root.style.setProperty('--mx', ((pointer.clientX - box.left) / box.width * 2 - 1).toFixed(3));
            root.style.setProperty('--my', ((pointer.clientY - box.top) / box.height * 2 - 1).toFixed(3));
            pointer = null;
          });
        });
      }

      /* Cursor: a round label that eases after the pointer over the doors. */
      if (motion && window.matchMedia && window.matchMedia('(pointer: fine)').matches) {
        var cursor = doc.createElement('span');
        cursor.className = 'bf-cursor';
        cursor.setAttribute('aria-hidden', 'true');
        root.appendChild(cursor);
        root.classList.add('bf--cursor');
        var px = 0, py = 0, cx = 0, cy = 0, moving = false, over = false;

        var follow = function () {
          cx += (px - cx) * 0.22;
          cy += (py - cy) * 0.22;
          cursor.style.transform = 'translate(' + cx.toFixed(1) + 'px, ' + cy.toFixed(1) + 'px)';
          if (over || Math.abs(px - cx) + Math.abs(py - cy) > 0.5) {
            window.requestAnimationFrame(follow);
          } else {
            moving = false;
          }
        };

        root.addEventListener('pointermove', function (event) {
          px = event.clientX;
          py = event.clientY;
          if (!moving) {
            moving = true;
            window.requestAnimationFrame(follow);
          }
        });

        doors.forEach(function (door) {
          each(door.leaves, function (leaf) {
            leaf.addEventListener('pointerenter', function (event) {
              if (!over) {
                // Start from the pointer, not from wherever it was last left.
                px = cx = event.clientX;
                py = cy = event.clientY;
              }
              over = true;
              cursor.setAttribute('data-state', door.state);
              cursor.textContent = root.getAttribute('data-cursor-' + door.state) || '';
              cursor.classList.add('is-on');
              if (!moving) {
                moving = true;
                window.requestAnimationFrame(follow);
              }
            });
            leaf.addEventListener('pointerleave', function () {
              over = false;
              cursor.classList.remove('is-on', 'is-down');
            });
            leaf.addEventListener('pointerdown', function () { cursor.classList.add('is-down'); });
            leaf.addEventListener('pointerup', function () { cursor.classList.remove('is-down'); });
            // Opening lifts the leaf away under the pointer.
            leaf.addEventListener('click', function () {
              if (door.state === 'live' || door.state === 'locked') {
                over = false;
                cursor.classList.remove('is-on', 'is-down');
              }
            });
          });
        });
      }

      var timer = null;
      function run() {
        if (timer) window.clearInterval(timer);
        timer = null;
        root.__bfTimer = null;
        if (!tick()) return;
        timer = window.setInterval(function () {
          if (!tick()) { window.clearInterval(timer); timer = null; }
        }, 1000);
        root.__bfTimer = timer;
      }

      run();

      /* Phone and tablet: the doors are a swipe rail. Its tabs jump to a
         door and follow the swipe, and it starts on the live door (or the
         next one up). On desktop the rail doesn't scroll, so this rests. */
      var rail = root.querySelector('.bf__doors');
      if (rail && rail.querySelector('[data-bf-door]') && root.querySelector('[data-bf-rail-to]')) {
        var railStart = function () { return parseFloat(window.getComputedStyle(rail).scrollPaddingLeft) || 0; };
        var railTo = function (door, smooth) {
          var left = door.el.offsetLeft - railStart();
          if (smooth && !prefersReducedMotion()) rail.scrollTo({ left: left, behavior: 'smooth' });
          else rail.scrollLeft = left;
        };
        var current = null;
        var markTab = function () {
          var x = rail.scrollLeft + railStart();
          var best = doors[0];
          if (rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 2) best = doors[doors.length - 1];
          else doors.forEach(function (door) {
            if (Math.abs(door.el.offsetLeft - x) < Math.abs(best.el.offsetLeft - x)) best = door;
          });
          if (best === current) return;
          current = best;
          doors.forEach(function (door) {
            if (!door.tab) return;
            if (door === best) door.tab.setAttribute('aria-current', 'true');
            else door.tab.removeAttribute('aria-current');
          });
        };
        var railQueued = false;
        rail.addEventListener('scroll', function () {
          if (railQueued) return;
          railQueued = true;
          window.requestAnimationFrame(function () { railQueued = false; markTab(); });
        }, { passive: true });
        doors.forEach(function (door) {
          if (door.tab) door.tab.addEventListener('click', function () { railTo(door, true); });
        });
        var lead = doors.filter(function (door) { return door.state === 'live'; })[0] ||
          doors.filter(function (door) { return door.el.hasAttribute('data-next'); })[0];
        if (lead && rail.scrollWidth > rail.clientWidth) railTo(lead, false);
        markTab();
      }
    }

    /* Free gift flash (snippets/black-friday-gift-flash.liquid): while the
       dates hold, each product card picture in the section gets the next
       flash from the template, again whenever the grid is swapped. */
    function setupFlash(tpl) {
      if (tpl.hasAttribute('data-bf-flash-ready')) return;
      tpl.setAttribute('data-bf-flash-ready', '');
      var start = parseUk(tpl.getAttribute('data-start'));
      var end = parseUk(tpl.getAttribute('data-end'));
      var t = Date.now();
      try {
        var raw = new URLSearchParams(window.location.search).get('bf_preview');
        var at = raw ? parseUk(raw.length === 10 ? raw + 'T00:00:01' : raw) : NaN;
        if (!isNaN(at)) t = at;
      } catch (err) { /* no URL preview */ }
      var always = tpl.hasAttribute('data-bf-flash-always');
      if (!always && (isNaN(start) || isNaN(end) || t < start || t > end)) return;
      var items = tpl.content ? tpl.content.querySelectorAll('[data-bf-flash-item]') : [];
      if (!items.length) return;
      var scope = tpl.closest('.shopify-section') || doc.body;
      var queued = false;

      function apply() {
        queued = false;
        each(scope.querySelectorAll('[data-product-card-media]'), function (media, i) {
          if (media.querySelector('.bf-flash')) return;
          if (getComputedStyle(media).position === 'static') media.style.position = 'relative';
          media.appendChild(items[i % items.length].cloneNode(true));
        });
      }

      apply();
      if (window.MutationObserver) {
        new MutationObserver(function () {
          if (queued) return;
          queued = true;
          window.setTimeout(apply, 60);
        }).observe(scope, { childList: true, subtree: true });
      }
    }

    /* Free tea picker (snippets/black-friday-free-tea.liquid): rendered
       hidden with its radios disabled; shown and enabled while the dates
       hold, so the property is only ever sent in season. */
    function setupTea(fieldset) {
      if (fieldset.hasAttribute('data-bf-tea-ready')) return;
      fieldset.setAttribute('data-bf-tea-ready', '');
      var start = parseUk(fieldset.getAttribute('data-start'));
      var end = parseUk(fieldset.getAttribute('data-end'));
      var t = Date.now();
      try {
        var raw = new URLSearchParams(window.location.search).get('bf_preview');
        var at = raw ? parseUk(raw.length === 10 ? raw + 'T00:00:01' : raw) : NaN;
        if (!isNaN(at)) t = at;
      } catch (err) { /* no URL preview */ }
      var always = fieldset.hasAttribute('data-bf-tea-always');
      if (!always && (isNaN(start) || isNaN(end) || t < start || t > end)) return;
      each(fieldset.querySelectorAll('input'), function (input) { input.disabled = false; });
      fieldset.hidden = false;
    }

    function boot(scope) {
      if (!scope || !scope.querySelectorAll) return;
      var list = Array.prototype.slice.call(scope.querySelectorAll('[data-bf]'));
      if (scope.matches && scope.matches('[data-bf]')) list.unshift(scope);
      list.forEach(function (root) {
        try {
          setup(root);
        } catch (err) {
          // Leave the server-rendered doors as they are, but say why.
          if (window.console) console.error('[suki] black friday advent', err);
        }
      });
      each(scope.querySelectorAll('[data-bf-tea]'), function (fieldset) {
        try { setupTea(fieldset); } catch (err) { if (window.console) console.error('[suki] black friday tea', err); }
      });
      each(scope.querySelectorAll('template[data-bf-flash]'), function (tpl) {
        try { setupFlash(tpl); } catch (err) { if (window.console) console.error('[suki] black friday flash', err); }
      });
    }

    boot(doc);
    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', function () { boot(doc); }, { once: true });
    }
    doc.addEventListener('shopify:section:load', function (event) { boot(event.target); });
    doc.addEventListener('shopify:section:unload', function (event) {
      each(event.target.querySelectorAll('[data-bf]'), function (root) {
        if (root.__bfTimer) window.clearInterval(root.__bfTimer);
      });
    });
  })();
}
